import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { RootState } from '../redux/store';
import { useSelector } from '../redux/hooks';

const PageContainer = styled.div`
  width: 100%;
  min-height: 100vh;
  padding: 40px;
  background: #ffffff;
`;

const Title = styled.h1`
  font-family: 'IBM Plex Mono', monospace;
  font-size: 32px;
  color: #a13b16;
  text-align: center;
  margin-bottom: 30px;
`;

const FlexRow = styled.div`
  display: flex;
  gap: 40px;
  align-items: flex-start;
`;

const List = styled.div`
  flex: 1;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-content: flex-start;
  row-gap: 10px;
`;

const ListItem = styled.div<{ $isHovered: boolean }>`
  padding: 8px 12px;
  border: 1px solid #ccc;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.2s;
  background: ${props => props.$isHovered ? '#f0e6e1' : '#f8f8f8'};
  white-space: nowrap;
`;

const Form = styled.form`
  flex: 0 0 300px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Input = styled.input`
  padding: 10px;
  border-radius: 8px;
  border: 1px solid #ccc;
  font-size: 16px;
`;

const Button = styled.button`
  padding: 10px;
  border: none;
  border-radius: 8px;
  background: #a13b16;
  color: white;
  font-weight: bold;
  cursor: pointer;
  transition: background 0.2s;
`;

const DangerButton = styled(Button)`
  background: #c0392b;
`;

const CancelButton = styled(Button)`
  background: #999;
`;

const TopBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

interface Language {
  language_id: number;
  name: string;
}

const LanguagesListView: React.FC = () => {
  const accessToken = useSelector((state: RootState) => state.auth.accessToken);
  const [languages, setLanguages] = useState<Language[]>([]);
  const [selectedLanguage, setSelectedLanguage] = useState<Language | null>(null);
  const [nameInput, setNameInput] = useState('');
  const [hoveredItem, setHoveredItem] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetch(`${process.env.REACT_APP_BASE_API_URL}/languages`)
      .then(res => {
        if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
        return res.json();
      })
      .then(data => setLanguages(data))
      .catch(err => console.error('Failed to fetch languages:', err));
  }, []);

  const handleSelect = useCallback((language: Language) => {
    setSelectedLanguage(language);
    setNameInput(language.name);
  }, []);

  const handleSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    if (!nameInput.trim()) return;

    const method = selectedLanguage ? "PUT" : "POST";
    const url = selectedLanguage
      ? `${process.env.REACT_APP_BASE_API_URL}/languages/${selectedLanguage.language_id}`
      : `${process.env.REACT_APP_BASE_API_URL}/languages`;

    fetch(url, {
      method,
      credentials: 'include',
      headers: { 
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({ name: nameInput })
    })
      .then(res => {
        if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
        return res.json();
      })
      .then(data => {
        if (selectedLanguage) {
          setLanguages(prev => prev.map(l => l.language_id === data.language_id ? data : l));
        } else {
          setLanguages(prev => [...prev, data]);
        }
      })
      .catch(err => console.error('Failed to save language:', err));

    setSelectedLanguage(null);
    setNameInput('');
  }, [accessToken, nameInput, selectedLanguage]);

  const handleDelete = useCallback(() => {
    if (!selectedLanguage) return;

    fetch(`${process.env.REACT_APP_BASE_API_URL}/languages/${selectedLanguage.language_id}`, {
      method: "DELETE",
      credentials: 'include',
      headers: { 
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      }
    })
      .then(res => {
        if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
        setLanguages(prev => prev.filter(l => l !== selectedLanguage));
        return res.json();
      })
      .catch(err => console.error('Failed to delete language:', err));

    setSelectedLanguage(null);
    setNameInput('');
  }, [accessToken, selectedLanguage]);

  const handleCancel = useCallback(() => {
    setSelectedLanguage(null);
    setNameInput('');
  }, []);

  return (
    <PageContainer>
      <TopBar>
        <Title>Мови</Title>
        <Button onClick={() => navigate('/admin')}>Головна</Button>
      </TopBar>
      <FlexRow>
        <List>
          {languages.map(language => (
            <ListItem
              key={language.language_id}
              $isHovered={hoveredItem === language.name}
              onClick={() => handleSelect(language)}
              onMouseEnter={() => setHoveredItem(language.name)}
              onMouseLeave={() => setHoveredItem(null)}
            >
              {language.name}
            </ListItem>
          ))}
        </List>

        <Form onSubmit={handleSubmit}>
          <Input
            placeholder="Назва мови"
            value={nameInput}
            onChange={e => setNameInput(e.target.value)}
          />
          <Button type="submit">
            {selectedLanguage ? 'Зберегти' : 'Створити'}
          </Button>
          {selectedLanguage && (
            <>
              <DangerButton type="button" onClick={handleDelete}>
                Видалити
              </DangerButton>
              <CancelButton type="button" onClick={handleCancel}>
                Відмінити
              </CancelButton>
            </>
          )}
        </Form>
      </FlexRow>
    </PageContainer>
  );
};

export default LanguagesListView;
